export interface ActionExecutionOptions {
	cooldownMs?: number;
	minBusyMs?: number;
}

/**
 * Framework-free dedup/cooldown tracker, keyed by a caller-supplied string.
 * Composed into `MxAction` (see action.ts) rather than injected as an Angular service, so
 * lifecycle state lives with the action it belongs to and needs no DI to unit test.
 *
 * There is deliberately no separate "busy" flag — a key is "busy" for exactly as long as
 * it's `isRunning`, no more and no less. Callers don't toggle it independently.
 */
export class ActionExecutionTracker {
	private readonly running = new Map<string, Promise<unknown>>();
	private readonly cooldownUntil = new Map<string, number>();

	isRunning(key: string): boolean {
		return this.running.has(key);
	}

	isCoolingDown(key: string): boolean {
		return (this.cooldownUntil.get(key) || 0) > Date.now();
	}

	getRunning<T = unknown>(key: string): Promise<T> | undefined {
		return this.running.get(key) as Promise<T> | undefined;
	}

	run<T>(key: string, factory: () => Promise<T>, options: ActionExecutionOptions = {}): Promise<T> {
		const existing = this.getRunning<T>(key);
		if (existing) {
			return existing;
		}
		if (this.isCoolingDown(key)) {
			return Promise.resolve(undefined as T);
		}

		const startedAt = Date.now();

		const execution = factory().finally(() => {
			const elapsed = Date.now() - startedAt;
			const remaining = Math.max(0, (options.minBusyMs || 0) - elapsed);
			const cleanup = () => {
				if (this.running.get(key) === execution) {
					this.running.delete(key);
					if (options.cooldownMs) {
						this.cooldownUntil.set(key, Date.now() + options.cooldownMs);
					}
				}
			};
			if (remaining > 0) {
				setTimeout(cleanup, remaining);
			} else {
				cleanup();
			}
		});

		this.running.set(key, execution);
		return execution;
	}
}
